/**
 * Row of homepage capability cards (landing-home-v2). Each card hands its
 * starter sentence to `onSelect`; the caller decides what to do with it
 * (the hero sends it as the founder's first onboarding chat message).
 */
"use client";

import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";

export type LandingCapabilityCardId = "validate_idea" | "early_gtm" | "fundraising";

export interface LandingCapabilityCard {
  id: LandingCapabilityCardId;
  title: string;
  description: string;
  /** Sent verbatim as a complete first message, so it must stand on its own. */
  starter: string;
}

export const LANDING_CAPABILITY_CARDS: readonly LandingCapabilityCard[] = [
  {
    id: "validate_idea",
    title: "Validating an idea",
    description: "Test the problem, customer and market before you build.",
    starter: "I have a startup idea and want to pressure-test it.",
  },
  {
    id: "early_gtm",
    title: "Finding early customers",
    description: "Find your first customers and the message that reaches them.",
    starter: "I want to find my first customers and the message that reaches them.",
  },
  {
    id: "fundraising",
    title: "Beginning fundraising",
    description: "Size your round and find investors who fit your stage.",
    starter: "I'm getting ready to raise and want help preparing.",
  },
];

export interface LandingCapabilityCardsProps {
  onSelect: (card: LandingCapabilityCard) => void;
  className?: string;
}

export function LandingCapabilityCards({ onSelect, className }: LandingCapabilityCardsProps) {
  return (
    <div className={cn("grid w-full grid-cols-1 gap-3 sm:grid-cols-3", className)}>
      {LANDING_CAPABILITY_CARDS.map((card) => (
        <button
          key={card.id}
          type="button"
          onClick={() => onSelect(card)}
          className={cn(
            "group flex flex-col gap-2 rounded-2xl border border-white/80 bg-white/70 p-4 text-left",
            "shadow-[0_1px_2px_rgba(45,49,57,0.04)] backdrop-blur-sm transition-colors",
            "hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-landing-dark/20",
          )}
        >
          <span className="flex items-start justify-between gap-2">
            <span className="text-sm font-semibold text-landing-dark">{card.title}</span>
            <ArrowUpRight
              className="h-4 w-4 flex-shrink-0 text-landing-secondary transition-colors group-hover:text-landing-dark"
              aria-hidden="true"
            />
          </span>
          <span className="text-[13px] leading-[1.45] text-landing-secondary">
            {card.description}
          </span>
        </button>
      ))}
    </div>
  );
}
